(() => {
  const canvas = document.querySelector('[data-foldinglink-glass-canvas]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const clamp01 = (value) => clamp(value, 0, 1);

  const updateHeroHandoff = () => {
    const hero = document.querySelector('.foldinglink-hero');
    const heroSticky = hero?.querySelector('.foldinglink-hero__sticky');
    const heroGlass = hero?.querySelector('.foldinglink-section-glass');
    if (!hero || !heroSticky) return;

    // Hero is normal document flow: no scroll-driven fade or visibility handoff.
    heroSticky.style.setProperty('--foldinglink-hero-handoff-opacity', '1');
    heroSticky.style.pointerEvents = '';
    heroSticky.style.visibility = '';
    if (heroGlass) heroGlass.dataset.foldinglinkGlassOpacity = '1';
  };

  if (!canvas) {
    updateHeroHandoff();
    return;
  }

  const parseNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseColor = (value, fallback) => {
    const source = String(value || fallback || '#000000').trim();
    const shortHex = /^#([0-9a-f]{3})$/i.exec(source);
    const longHex = /^#([0-9a-f]{6})$/i.exec(source);
    let hex = null;

    if (shortHex) {
      hex = shortHex[1].split('').map((char) => char + char).join('');
    } else if (longHex) {
      hex = longHex[1];
    }

    if (!hex) return [0, 0, 0];

    return [
      parseInt(hex.slice(0, 2), 16) / 255,
      parseInt(hex.slice(2, 4), 16) / 255,
      parseInt(hex.slice(4, 6), 16) / 255,
    ];
  };

  const settings = {
    backgroundTop: parseColor(canvas.dataset.backgroundTop, '#ffffff'),
    backgroundBottom: parseColor(canvas.dataset.backgroundBottom, '#c5c6c8'),
    ballsEnabled: canvas.dataset.ballsEnabled !== 'false',
    ballDeep: parseColor(canvas.dataset.ballColorDeep, '#0e47a3'),
    ballCore: parseColor(canvas.dataset.ballColorCore, '#156cf9'),
    ballSoft: parseColor(canvas.dataset.ballColorSoft, '#8bb1f8'),
    ballEdge: parseColor(canvas.dataset.ballColorEdge, '#b4ceff'),
    ballOpacity: clamp(parseNumber(canvas.dataset.ballOpacity, 70) / 100, 0, 1),
    motionStrength: Math.max(0, parseNumber(canvas.dataset.motionStrength, 100) / 100),
    glassEnabled: canvas.dataset.glassEnabled !== 'false',
    frost: clamp(parseNumber(canvas.dataset.frost, 12), 0, 24),
    panelRadius: clamp(parseNumber(canvas.dataset.panelRadius, 24), 0, 32),
    edgeWidth: clamp(parseNumber(canvas.dataset.edgeRange, 7) * (24 / 7), 1, 80),
    refractionPull: clamp(parseNumber(canvas.dataset.refractionIntensity, 300) / 12.5, 0, 80),
    // Rendering resolution is an internal bounded policy, not merchant content.
    blurScale: 0.5,
    desktopDpr: 1.35,
    mobileDpr: 1.15,
  };

  const gl = canvas.getContext('webgl2', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    preserveDrawingBuffer: false,
    powerPreference: 'high-performance',
  });

  const enableFallback = () => {
    document.documentElement.classList.add('foldinglink-glass-fallback');
    canvas.hidden = true;
  };

  if (!gl) {
    enableFallback();
    updateHeroHandoff();
    return;
  }

  const MAX_GLASS = 16;
  const vertexSource = `#version 300 es
    precision highp float;
    out vec2 vUv;

    const vec2 P[3] = vec2[3](
      vec2(-1.0, -1.0),
      vec2( 3.0, -1.0),
      vec2(-1.0,  3.0)
    );

    void main() {
      vec2 p = P[gl_VertexID];
      vUv = p * 0.5 + 0.5;
      gl_Position = vec4(p, 0.0, 1.0);
    }
  `;

  const sceneFunction = `
    uniform vec3 uBgTop;
    uniform vec3 uBgBottom;
    uniform vec3 uBallDeep;
    uniform vec3 uBallCore;
    uniform vec3 uBallSoft;
    uniform vec3 uBallEdge;
    uniform float uBallOpacity;
    uniform float uBallsEnabled;
    uniform vec2 uBallCenters[4];
    uniform float uBallRadii[4];
    uniform vec2 uMeshCenters[4];

    float bloomAlpha(vec2 uvTop, vec2 center, float radius, float aspect) {
      vec2 delta = vec2((uvTop.x - center.x) * aspect, uvTop.y - center.y);
      float distanceRatio = clamp(length(delta) / radius, 0.0, 1.0);
      return 0.5 + 0.5 * cos(3.14159265359 * distanceRatio);
    }

    float grainHash(vec2 pixel) {
      return fract(sin(dot(pixel, vec2(12.9898, 78.233))) * 43758.5453);
    }

    vec3 overlayBlend(vec3 base, float grain) {
      vec3 low = 2.0 * base * grain;
      vec3 high = 1.0 - 2.0 * (1.0 - base) * (1.0 - grain);
      return mix(low, high, step(vec3(0.5), base));
    }

    vec3 bloomField(vec2 uvTop, vec2 cssSize) {
      const vec3 MAUVE = vec3(0.8863, 0.8863, 0.8863);
      const vec3 GLAZED_AZURE = vec3(0.1059, 0.6235, 0.9961);
      const vec3 SKY = vec3(0.2902, 0.7882, 1.0);
      float aspect = cssSize.x / cssSize.y;
      vec3 color = MAUVE;

      color = mix(color, SKY, bloomAlpha(uvTop, uMeshCenters[3], 0.411, aspect));
      color = mix(color, GLAZED_AZURE, bloomAlpha(uvTop, uMeshCenters[2], 0.670, aspect));
      color = mix(color, GLAZED_AZURE, bloomAlpha(uvTop, uMeshCenters[1], 0.516, aspect));
      color = mix(color, MAUVE, bloomAlpha(uvTop, uMeshCenters[0], 0.761, aspect));

      float grain = grainHash(floor(uvTop * cssSize));
      return mix(color, overlayBlend(color, grain), 0.18);
    }

    vec3 spherePalette(float value) {
      float t = clamp(value, 0.0, 1.0);
      float u = 1.0 - t;
      return
        (u * u * u) * uBallDeep +
        (3.0 * u * u * t) * uBallCore +
        (3.0 * u * t * t) * uBallSoft +
        (t * t * t) * uBallEdge;
    }

    vec3 scene(vec2 uvTop, vec2 cssSize) {
      vec3 bg = bloomField(uvTop, cssSize);

      if (uBallsEnabled < 0.5 || uBallOpacity <= 0.001) return bg;

      float aspect = cssSize.x / cssSize.y;
      vec2 c1 = uBallCenters[0];
      vec2 c2 = uBallCenters[1];
      vec2 c3 = uBallCenters[2];
      vec2 c4 = uBallCenters[3];

      float r1 = uBallRadii[0];
      float r2 = uBallRadii[1];
      float r3 = uBallRadii[2];
      float r4 = uBallRadii[3];

      vec2 d1 = vec2((uvTop.x - c1.x) * aspect, uvTop.y - c1.y);
      vec2 d2 = vec2((uvTop.x - c2.x) * aspect, uvTop.y - c2.y);
      vec2 d3 = vec2((uvTop.x - c3.x) * aspect, uvTop.y - c3.y);
      vec2 d4 = vec2((uvTop.x - c4.x) * aspect, uvTop.y - c4.y);

      float aa = 1.25 / cssSize.y;
      float m1 = 1.0 - smoothstep(r1 - aa, r1 + aa, length(d1));
      float m2 = 1.0 - smoothstep(r2 - aa, r2 + aa, length(d2));
      float m3 = 1.0 - smoothstep(r3 - aa, r3 + aa, length(d3));
      float m4 = 1.0 - smoothstep(r4 - aa, r4 + aa, length(d4));

      vec2 focal = vec2(-0.4238, 0.4266);
      vec2 local1 = d1 / r1;
      vec2 local2 = d2 / r2;
      vec2 local3 = d3 / r3;
      vec2 local4 = d4 / r4;

      float g1 = length(local1 - focal) / 1.2176;
      float g2 = length(local2 - focal) / 1.2176;
      float g3 = length(local3 - focal) / 1.2176;
      float g4 = length(local4 - focal) / 1.2176;
      vec3 ball1 = spherePalette(g1);
      vec3 ball2 = spherePalette(g2);
      vec3 ball3 = spherePalette(g3);
      vec3 ball4 = spherePalette(g4);

      vec3 sceneColor = bg;
      sceneColor = mix(sceneColor, ball1, m1 * uBallOpacity);
      sceneColor = mix(sceneColor, ball2, m2 * uBallOpacity);
      sceneColor = mix(sceneColor, ball3, m3 * uBallOpacity);
      sceneColor = mix(sceneColor, ball4, m4 * uBallOpacity);
      return sceneColor;
    }
  `;

  const sceneFragment = `#version 300 es
    precision highp float;
    in vec2 vUv;
    out vec4 outColor;

    uniform vec2 uCssSize;
    ${sceneFunction}

    void main() {
      vec2 uvTop = vec2(vUv.x, 1.0 - vUv.y);
      outColor = vec4(scene(uvTop, uCssSize), 1.0);
    }
  `;

  const blurFragment = `#version 300 es
    precision highp float;
    in vec2 vUv;
    out vec4 outColor;

    uniform sampler2D uSource;
    uniform vec2 uStep;

    void main() {
      vec3 c = vec3(0.0);
      c += texture(uSource, vUv - 3.0 * uStep).rgb * 1.0;
      c += texture(uSource, vUv - 2.0 * uStep).rgb * 6.0;
      c += texture(uSource, vUv - 1.0 * uStep).rgb * 15.0;
      c += texture(uSource, vUv).rgb                 * 20.0;
      c += texture(uSource, vUv + 1.0 * uStep).rgb * 15.0;
      c += texture(uSource, vUv + 2.0 * uStep).rgb * 6.0;
      c += texture(uSource, vUv + 3.0 * uStep).rgb * 1.0;
      outColor = vec4(c / 64.0, 1.0);
    }
  `;

  const compositeFragment = `#version 300 es
    precision highp float;
    in vec2 vUv;
    out vec4 outColor;

    #define MAX_GLASS ${MAX_GLASS}

    uniform sampler2D uBlurred;
    uniform vec2 uCssSize;
    uniform float uDpr;
    uniform vec4 uGlassRects[MAX_GLASS];
    uniform float uGlassRadii[MAX_GLASS];
    uniform float uGlassAlphas[MAX_GLASS];
    uniform int uGlassCount;
    uniform float uEdge;
    uniform float uPull;
    ${sceneFunction}

    float sdRoundBox(vec2 p, vec2 b, float r) {
      vec2 q = abs(p) - b + vec2(r);
      return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
    }

    vec2 roundedBoxNormal(vec2 p, vec2 b, float r) {
      vec2 axisSign = mix(vec2(-1.0), vec2(1.0), step(vec2(0.0), p));
      vec2 q = abs(p) - b + vec2(r);
      vec2 corner = max(q, 0.0);
      float cornerLength = length(corner);

      if (cornerLength > 1e-5) return (corner / cornerLength) * axisSign;
      return q.x > q.y ? vec2(axisSign.x, 0.0) : vec2(0.0, axisSign.y);
    }

    // Convex squircle surface from sohumsuthar/liquid-glass (MIT).
    // Refraction follows the normalized surface slope instead of a color-like bell curve.
    float convexSquircleRefraction(float t) {
      float inverseT = 1.0 - clamp(t, 0.0, 1.0);
      float inverseT2 = inverseT * inverseT;
      float inverseT4 = inverseT2 * inverseT2;
      float surfaceBase = max(1.0 - inverseT4, 1e-6);
      float slope = (inverseT2 * inverseT) / pow(surfaceBase, 0.75);
      return (2.0 * slope) / (1.0 + slope * slope);
    }

    void main() {
      vec2 frag = vec2(
        gl_FragCoord.x / uDpr,
        uCssSize.y - gl_FragCoord.y / uDpr
      );
      vec2 uvTop = frag / uCssSize;
      vec3 color = scene(uvTop, uCssSize);

      for (int i = 0; i < MAX_GLASS; i++) {
        if (i >= uGlassCount) continue;

        vec4 rect = uGlassRects[i];
        vec2 rectMin = rect.xy;
        vec2 rectSize = rect.zw;
        vec2 center = rectMin + rectSize * 0.5;
        vec2 local = frag - center;
        vec2 halfSize = rectSize * 0.5;
        float radius = min(uGlassRadii[i], min(halfSize.x, halfSize.y));
        float glassAlpha = clamp(uGlassAlphas[i], 0.0, 1.0);
        float sdf = sdRoundBox(local, halfSize, radius);

        float edgeAA = max(fwidth(sdf), 0.5 / uDpr);
        float glassCoverage = 1.0 - smoothstep(-edgeAA, edgeAA, sdf);

        if (glassCoverage > 0.001) {
          float edgeDist = max(-sdf, 0.0);
          vec2 sampleCss = frag;

          if (edgeDist < uEdge) {
            float lensT = edgeDist / uEdge;
            float lensProfile = convexSquircleRefraction(lensT);
            vec2 outward = roundedBoxNormal(local, halfSize, radius);
            sampleCss -= outward * (uPull * lensProfile);
          }

          vec2 sampleTop = clamp(sampleCss / uCssSize, vec2(0.0), vec2(1.0));
          vec2 blurUv = vec2(sampleTop.x, 1.0 - sampleTop.y);

          vec3 glassColor = texture(uBlurred, blurUv).rgb;

          color = mix(color, glassColor, glassAlpha * glassCoverage);
        }
      }

      outColor = vec4(color, 1.0);
    }
  `;

  const compileShader = (type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader) || 'shader compile failed';
      gl.deleteShader(shader);
      throw new Error(log);
    }

    return shader;
  };

  const createProgram = (fragmentSource) => {
    const program = gl.createProgram();
    const vertexShader = compileShader(gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = compileShader(gl.FRAGMENT_SHADER, fragmentSource);

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) || 'program link failed';
      gl.deleteProgram(program);
      throw new Error(log);
    }

    return program;
  };

  const createSceneUniforms = (program) => ({
    cssSize: gl.getUniformLocation(program, 'uCssSize'),
    bgTop: gl.getUniformLocation(program, 'uBgTop'),
    bgBottom: gl.getUniformLocation(program, 'uBgBottom'),
    ballDeep: gl.getUniformLocation(program, 'uBallDeep'),
    ballCore: gl.getUniformLocation(program, 'uBallCore'),
    ballSoft: gl.getUniformLocation(program, 'uBallSoft'),
    ballEdge: gl.getUniformLocation(program, 'uBallEdge'),
    ballOpacity: gl.getUniformLocation(program, 'uBallOpacity'),
    ballsEnabled: gl.getUniformLocation(program, 'uBallsEnabled'),
    ballCenters: gl.getUniformLocation(program, 'uBallCenters[0]'),
    ballRadii: gl.getUniformLocation(program, 'uBallRadii[0]'),
    meshCenters: gl.getUniformLocation(program, 'uMeshCenters[0]'),
  });

  let sceneProgram;
  let blurProgram;
  let compositeProgram;

  try {
    sceneProgram = createProgram(sceneFragment);
    blurProgram = createProgram(blurFragment);
    compositeProgram = createProgram(compositeFragment);
  } catch (error) {
    console.error('[FoldingLink glass] WebGL shader initialization failed:', error);
    enableFallback();
    updateHeroHandoff();
    return;
  }

  const sceneUniforms = createSceneUniforms(sceneProgram);
  const compositeSceneUniforms = createSceneUniforms(compositeProgram);
  const blurUniforms = {
    source: gl.getUniformLocation(blurProgram, 'uSource'),
    step: gl.getUniformLocation(blurProgram, 'uStep'),
  };
  const compositeUniforms = {
    blurred: gl.getUniformLocation(compositeProgram, 'uBlurred'),
    dpr: gl.getUniformLocation(compositeProgram, 'uDpr'),
    glassRects: gl.getUniformLocation(compositeProgram, 'uGlassRects[0]'),
    glassRadii: gl.getUniformLocation(compositeProgram, 'uGlassRadii[0]'),
    glassAlphas: gl.getUniformLocation(compositeProgram, 'uGlassAlphas[0]'),
    glassCount: gl.getUniformLocation(compositeProgram, 'uGlassCount'),
    edge: gl.getUniformLocation(compositeProgram, 'uEdge'),
    pull: gl.getUniformLocation(compositeProgram, 'uPull'),
  };

  const createRenderTarget = () => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const framebuffer = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture,
      0,
    );

    return { texture, framebuffer, width: 0, height: 0 };
  };

  const sceneTarget = createRenderTarget();
  const tempTarget = createRenderTarget();
  const blurTarget = createRenderTarget();

  const resizeTarget = (target, width, height) => {
    if (target.width === width && target.height === height) return;
    target.width = width;
    target.height = height;
    gl.bindTexture(gl.TEXTURE_2D, target.texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA8,
      width,
      height,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null,
    );
  };

  const bindTarget = (target, width, height) => {
    gl.bindFramebuffer(gl.FRAMEBUFFER, target ? target.framebuffer : null);
    gl.viewport(0, 0, width, height);
  };

  const bindTexture = (unit, texture) => {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
  };

  const ballFrame = {
    centers: new Float32Array(8),
    radii: new Float32Array(4),
  };
  const meshFrame = { centers: new Float32Array(8) };
  const meshBaseCenters = [
    0.6704, 0.4593,
    0.3547, 0.6592,
    0.4833, 0.2011,
    0.8081, 0.8803,
  ];
  const meshSeed = 174074637;
  const meshPhase = (salt) => {
    const hash = Math.sin(meshSeed + salt * 12.9898) * 43758.5453;
    return (hash - Math.floor(hash)) * Math.PI * 2;
  };
  const meshPhases = Array.from({ length: 8 }, (_, index) => meshPhase(index + 1));
  const depthSpeeds = [0.39, 0.46, 0.34, 0.42];
  const depthPhases = [0.4, 2.1, 4.0, 5.4];

  const updateMeshFrame = (time) => {
    const ph = reducedMotion.matches ? 0 : time * 1.00;
    const amt = reducedMotion.matches ? 0 : 1.00;
    const dir = 1;
    const spin = ph * dir;
    const centers = meshFrame.centers;

    for (let index = 0; index < 4; index += 1) {
      const p = meshPhases[index * 2];
      const p2 = meshPhases[index * 2 + 1];
      centers[index * 2] = meshBaseCenters[index * 2]
        + (Math.sin(spin * 0.55 + p) - Math.sin(p)) * 0.14 * amt;
      centers[index * 2 + 1] = meshBaseCenters[index * 2 + 1]
        + (Math.sin(spin * 0.43 + p2) - Math.sin(p2)) * 0.14 * amt;
    }

    return meshFrame;
  };

  const updateBallFrame = (time, cssW) => {
    const animated = !reducedMotion.matches;
    const t = animated ? time : 0;
    const motion = animated ? settings.motionStrength : 0;
    const centers = ballFrame.centers;

    // The fixed canvas is the motion stage. Keeping every centre inside its
    // 0–1 viewport bounds means no more than half a sphere can leave an edge.
    centers[0] = clamp(0.88 + Math.sin(t * 0.42) * 0.10 * motion, 0, 1);
    centers[1] = clamp(0.10 + Math.cos(t * 0.31) * 0.08 * motion, 0, 1);
    centers[2] = clamp(0.07 + Math.sin(t * 0.36 + 1.4) * 0.10 * motion, 0, 1);
    centers[3] = clamp(0.38 + Math.cos(t * 0.47 + 0.7) * 0.12 * motion, 0, 1);
    centers[4] = clamp(0.12 + Math.sin(t * 0.29 + 2.1) * 0.11 * motion, 0, 1);
    centers[5] = clamp(0.90 + Math.cos(t * 0.34 + 1.8) * 0.09 * motion, 0, 1);
    centers[6] = clamp(0.92 + Math.sin(t * 0.55 + 0.2) * 0.07 * motion, 0, 1);
    centers[7] = clamp(0.76 + Math.cos(t * 0.40 + 2.8) * 0.10 * motion, 0, 1);

    const mobile = cssW <= 768;
    const physicalRadius = mobile ? 0.105 : 0.115;
    const zCenter = mobile ? 1.04 : 1.11;
    const zAmplitude = (mobile ? 0.16 : 0.28) * Math.min(motion, 1);

    for (let index = 0; index < 4; index += 1) {
      const z = zCenter + Math.sin(t * depthSpeeds[index] + depthPhases[index]) * zAmplitude;
      ballFrame.radii[index] = physicalRadius / z;
    }

    return ballFrame;
  };

  const setSceneUniforms = (uniforms, cssW, cssH, balls, mesh) => {
    gl.uniform2f(uniforms.cssSize, cssW, cssH);
    gl.uniform3fv(uniforms.bgTop, settings.backgroundTop);
    gl.uniform3fv(uniforms.bgBottom, settings.backgroundBottom);
    gl.uniform3fv(uniforms.ballDeep, settings.ballDeep);
    gl.uniform3fv(uniforms.ballCore, settings.ballCore);
    gl.uniform3fv(uniforms.ballSoft, settings.ballSoft);
    gl.uniform3fv(uniforms.ballEdge, settings.ballEdge);
    gl.uniform1f(uniforms.ballOpacity, settings.ballOpacity);
    gl.uniform1f(uniforms.ballsEnabled, settings.ballsEnabled ? 1 : 0);
    gl.uniform2fv(uniforms.ballCenters, balls.centers);
    gl.uniform1fv(uniforms.ballRadii, balls.radii);
    gl.uniform2fv(uniforms.meshCenters, mesh.centers);
  };

  let glassElements = [];
  let resizeObserver = null;
  let needsRender = true;

  const collectGlassElements = () => {
    resizeObserver?.disconnect();
    glassElements = Array.from(document.querySelectorAll('.foldinglink-section-glass')).slice(0, MAX_GLASS);

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        needsRender = true;
      });
      glassElements.forEach((element) => resizeObserver.observe(element));
    }

    needsRender = true;
  };

  const ensureSize = () => {
    const canvasRect = canvas.getBoundingClientRect();
    const cssW = Math.max(1, canvasRect.width);
    const cssH = Math.max(1, canvasRect.height);
    const dprCap = cssW <= 768 ? settings.mobileDpr : settings.desktopDpr;
    const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
    const outW = Math.max(1, Math.round(cssW * dpr));
    const outH = Math.max(1, Math.round(cssH * dpr));

    if (canvas.width !== outW || canvas.height !== outH) {
      canvas.width = outW;
      canvas.height = outH;
    }

    const blurW = Math.max(2, Math.round(outW * settings.blurScale));
    const blurH = Math.max(2, Math.round(outH * settings.blurScale));
    resizeTarget(sceneTarget, blurW, blurH);
    resizeTarget(tempTarget, blurW, blurH);
    resizeTarget(blurTarget, blurW, blurH);

    return { cssW, cssH, dpr, outW, outH, blurW, blurH };
  };

  const getVisibleGlassData = (cssW, cssH) => {
    const rects = new Float32Array(MAX_GLASS * 4);
    const radii = new Float32Array(MAX_GLASS);
    const alphas = new Float32Array(MAX_GLASS);
    let count = 0;

    for (const element of glassElements) {
      if (count >= MAX_GLASS) break;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 1 || rect.height <= 1) continue;
      if (rect.right <= 0 || rect.left >= cssW || rect.bottom <= 0 || rect.top >= cssH) continue;

      const style = getComputedStyle(element);
      const radius = parseFloat(style.borderTopLeftRadius) || 0;
      const alpha = clamp(parseNumber(element.dataset.foldinglinkGlassOpacity, 1), 0, 1);
      if (alpha <= 0.001) continue;
      const offset = count * 4;
      rects[offset] = rect.left;
      rects[offset + 1] = rect.top;
      rects[offset + 2] = rect.width;
      rects[offset + 3] = rect.height;
      radii[count] = radius;
      alphas[count] = alpha;
      count += 1;
    }

    return { rects, radii, alphas, count };
  };

  const startTime = performance.now();

  const render = (now = performance.now()) => {
    const size = ensureSize();
    const time = (now - startTime) * 0.001;
    const balls = updateBallFrame(time, size.cssW);
    const mesh = updateMeshFrame(time);
    const glass = getVisibleGlassData(size.cssW, size.cssH);

    if (!settings.glassEnabled) {
      bindTarget(null, size.outW, size.outH);
      gl.useProgram(sceneProgram);
      setSceneUniforms(sceneUniforms, size.cssW, size.cssH, balls, mesh);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      return;
    }

    // Pass 1: fixed background scene at reduced resolution.
    bindTarget(sceneTarget, size.blurW, size.blurH);
    gl.useProgram(sceneProgram);
    setSceneUniforms(sceneUniforms, size.cssW, size.cssH, balls, mesh);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // Pass 2 + 3: separable 7-tap blur on the reduced-resolution scene.
    const stepPx = settings.frost / 3;

    bindTarget(tempTarget, size.blurW, size.blurH);
    gl.useProgram(blurProgram);
    bindTexture(0, sceneTarget.texture);
    gl.uniform1i(blurUniforms.source, 0);
    gl.uniform2f(blurUniforms.step, stepPx / size.cssW, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    bindTarget(blurTarget, size.blurW, size.blurH);
    gl.useProgram(blurProgram);
    bindTexture(0, tempTarget.texture);
    gl.uniform1i(blurUniforms.source, 0);
    gl.uniform2f(blurUniforms.step, 0, stepPx / size.cssH);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // Pass 4: one full-resolution composite for every currently visible glass panel.
    bindTarget(null, size.outW, size.outH);
    gl.useProgram(compositeProgram);
    setSceneUniforms(compositeSceneUniforms, size.cssW, size.cssH, balls, mesh);
    bindTexture(0, blurTarget.texture);
    gl.uniform1i(compositeUniforms.blurred, 0);
    gl.uniform1f(compositeUniforms.dpr, size.dpr);
    gl.uniform4fv(compositeUniforms.glassRects, glass.rects);
    gl.uniform1fv(compositeUniforms.glassRadii, glass.radii);
    gl.uniform1fv(compositeUniforms.glassAlphas, glass.alphas);
    gl.uniform1i(compositeUniforms.glassCount, glass.count);
    const radiusCap = size.cssW <= 767
      ? Math.min(settings.panelRadius, 16)
      : size.cssW <= 1199
        ? Math.min(settings.panelRadius, 20)
        : settings.panelRadius;
    const edgeScale = size.cssW <= 767 ? 0.66 : size.cssW <= 1199 ? 0.84 : 1;
    gl.uniform1f(compositeUniforms.edge, Math.min(settings.edgeWidth * edgeScale, Math.max(radiusCap, 0.001)));
    gl.uniform1f(compositeUniforms.pull, size.cssW <= 768 ? settings.refractionPull * 0.74 : settings.refractionPull);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  let rafId = 0;
  let lastRender = 0;
  const mobileFrameInterval = 1000 / 45;
  const desktopFrameInterval = 1000 / 60;

  const tick = (now) => {
    rafId = 0;
    if (document.hidden) return;

    const animateScene = !reducedMotion.matches;
    const interval = window.innerWidth <= 768 ? mobileFrameInterval : desktopFrameInterval;

    if ((needsRender || animateScene) && now - lastRender >= interval) {
      updateHeroHandoff();
      render(now);
      lastRender = now;
      needsRender = false;
    }

    if (animateScene || needsRender) {
      rafId = window.requestAnimationFrame(tick);
    }
  };

  const start = () => {
    if (!rafId && !document.hidden) rafId = window.requestAnimationFrame(tick);
  };

  const requestRender = () => {
    needsRender = true;
    start();
  };

  window.addEventListener('scroll', requestRender, { passive: true });
  window.addEventListener('resize', requestRender, { passive: true });
  reducedMotion.addEventListener?.('change', requestRender);
  document.addEventListener('shopify:section:load', collectGlassElements);
  document.addEventListener('shopify:section:unload', collectGlassElements);
  document.addEventListener('shopify:section:reorder', collectGlassElements);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = 0;
      return;
    }
    requestRender();
  });

  canvas.addEventListener('webglcontextlost', (event) => {
    event.preventDefault();
    enableFallback();
  });

  collectGlassElements();
  updateHeroHandoff();
  render();
  start();
})();
